import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import { GBP_ENABLED, SOCIAL_ENABLED } from '../lib/featureFlags';
import { createBusinessCache } from '../lib/controlCentre/actionsCache';
import {
  buildMarketingAiTokensRequiredActions,
  type MarketingAiTokensSnapshot,
  type MarketingAutomationFlags,
} from '../lib/marketing/marketingAiTokensRequiredActions';

type CachePayload = {
  tokens: MarketingAiTokensSnapshot | null;
  flags: MarketingAutomationFlags;
};

const tokensAutomationCache = createBusinessCache<CachePayload>({
  persistKey: 'marketing-ai-tokens',
});

const EMPTY_FLAGS: MarketingAutomationFlags = {
  gbpAutoPost: false,
  gbpAutoReply: false,
  gbpReviewInstagramShare: false,
  socialAutoPost: false,
};

async function fetchActiveConnectionIds(
  client: SupabaseClient,
  businessId: string,
  table: 'google_business_connections' | 'social_media_connections',
): Promise<string[]> {
  const { data, error } = await client
    .from(table)
    .select('id')
    .eq('business_id', businessId)
    .in('provider', table === 'google_business_connections' ? ['ayrshare', 'google'] : ['ayrshare'])
    .eq('is_active', true);
  if (error) throw error;
  return (data || []).map((r) => r.id as string);
}

async function anyEnabledForConnections(
  client: SupabaseClient,
  table:
    | 'google_business_auto_post_settings'
    | 'google_business_auto_reply_settings'
    | 'google_business_review_instagram_settings'
    | 'social_auto_post_settings',
  connectionIds: string[],
): Promise<boolean> {
  if (connectionIds.length === 0) return false;
  const { data, error } = await client
    .from(table)
    .select('connection_id')
    .in('connection_id', connectionIds)
    .eq('enabled', true)
    .limit(1);
  if (error) throw error;
  return (data || []).length > 0;
}

/** Marketing auto-post/reply blocked by low AI tokens or failed auto-topup. */
export function useMarketingAiTokensRequiredActions(enabled = true) {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? '';

  const [payload, setPayload] = useState<CachePayload>(
    () => tokensAutomationCache.get(businessId) ?? { tokens: null, flags: EMPTY_FLAGS },
  );
  const [isLoading, setIsLoading] = useState(() => !tokensAutomationCache.has(businessId));
  const [error, setError] = useState<string | null>(null);

  const fetchSnapshot = useCallback(async () => {
    if (!businessId || !supabase) return;

    // Plan allowance remaining + purchased tokens: what the automations can actually spend.
    const { data: tokenRow, error: tokenError } = await supabase
      .from('ai_token_budgets')
      .select('available_tokens, auto_topup_paused_reason, updated_at')
      .eq('business_id', businessId)
      .maybeSingle();
    if (tokenError) throw tokenError;

    const tokens: MarketingAiTokensSnapshot | null = tokenRow
      ? {
          tokenBalance: Number(tokenRow.available_tokens) || 0,
          pausedReason: tokenRow.auto_topup_paused_reason ?? null,
          updatedAt: tokenRow.updated_at ?? null,
        }
      : { tokenBalance: 0, pausedReason: null, updatedAt: null };

    let gbpAutoPost = false;
    let gbpAutoReply = false;
    let gbpReviewInstagramShare = false;
    let socialAutoPost = false;

    if (GBP_ENABLED) {
      const gbpIds = await fetchActiveConnectionIds(
        supabase,
        businessId,
        'google_business_connections',
      );
      const [postsOn, replyOn, reviewIgOn] = await Promise.all([
        anyEnabledForConnections(supabase, 'google_business_auto_post_settings', gbpIds),
        anyEnabledForConnections(supabase, 'google_business_auto_reply_settings', gbpIds),
        anyEnabledForConnections(
          supabase,
          'google_business_review_instagram_settings',
          gbpIds,
        ),
      ]);
      gbpAutoPost = postsOn;
      gbpAutoReply = replyOn;
      gbpReviewInstagramShare = reviewIgOn;
    }

    if (SOCIAL_ENABLED) {
      const socialIds = await fetchActiveConnectionIds(
        supabase,
        businessId,
        'social_media_connections',
      );
      socialAutoPost = await anyEnabledForConnections(
        supabase,
        'social_auto_post_settings',
        socialIds,
      );
    }

    const next: CachePayload = {
      tokens,
      flags: { gbpAutoPost, gbpAutoReply, gbpReviewInstagramShare, socialAutoPost },
    };
    setPayload(next);
    tokensAutomationCache.set(businessId, next);
  }, [businessId, supabase]);

  const load = useCallback(async () => {
    if (!enabled || !businessId || !supabase) {
      setPayload({ tokens: null, flags: EMPTY_FLAGS });
      setIsLoading(false);
      return;
    }

    const hasCache = tokensAutomationCache.has(businessId);
    if (hasCache) {
      setPayload(tokensAutomationCache.get(businessId) ?? { tokens: null, flags: EMPTY_FLAGS });
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      await fetchSnapshot();
    } catch (err) {
      console.error('Error loading marketing AI token required actions:', err);
      setError('Failed to load marketing automation token actions.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, businessId, supabase, fetchSnapshot]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setPayload({ tokens: null, flags: EMPTY_FLAGS });
      return;
    }
    void load();
  }, [load, enabled]);

  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!enabled || !businessId || !supabase || isLoading) return;

    const scheduleRefresh = () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      refreshDebounceRef.current = setTimeout(() => {
        void fetchSnapshot();
      }, 400);
    };

    const channel = supabase
      .channel(`marketing-ai-tokens-required-actions-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ai_tokens',
          filter: `business_id=eq.${businessId}`,
        },
        scheduleRefresh,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'google_business_auto_post_settings' },
        scheduleRefresh,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'google_business_auto_reply_settings' },
        scheduleRefresh,
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'google_business_review_instagram_settings',
        },
        scheduleRefresh,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'social_auto_post_settings' },
        scheduleRefresh,
      )
      .subscribe();

    return () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      void supabase.removeChannel(channel);
    };
  }, [enabled, businessId, supabase, isLoading, fetchSnapshot]);

  const marketingAiTokensActions = useMemo(
    () => buildMarketingAiTokensRequiredActions(payload.tokens, payload.flags),
    [payload],
  );

  return {
    marketingAiTokensActions,
    isLoading,
    error,
    refresh: load,
  };
}
